import { formatDistanceToNow, format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import i18n from '@/lib/i18n/config';

/** Active date-fns locale, tracking the i18next chrome language (spec §5.3 / §6). */
function activeDateLocale() {
  return i18n.language === 'ar' ? ar : enUS;
}

/** Human-readable byte size with thin-space unit (DESIGN numeric voice). */
export function formatBytes(bytes: number): string {
  if (!bytes) return '0\u2009B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)}\u2009${units[i]}`;
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: activeDateLocale() });
  } catch {
    return '—';
  }
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'yyyy-MM-dd HH:mm');
  } catch {
    return '—';
  }
}

/** A day, `yyyy-MM-dd` — the shape every date field on a record already shows. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'yyyy-MM-dd');
  } catch {
    return '—';
  }
}

/**
 * A calendar day (`YYYY-MM-DD`) in the reader's language: `long` is "Saturday,
 * 14 November 2026" (السبت، 14 نوفمبر 2026), `short` is "14 Nov". Read in UTC,
 * because a day has no zone and a local read would shift it a day west of
 * Greenwich. Null for anything that is not an ISO day, so the caller keeps its
 * own fallback.
 */
export function formatDay(
  iso: string | null | undefined,
  locale: string | undefined,
  style: 'long' | 'short' = 'long',
): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(
    locale === 'ar' ? 'ar' : 'en-GB',
    style === 'long'
      ? { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }
      : { day: 'numeric', month: 'short', timeZone: 'UTC' },
  ).format(d);
}

/**
 * A wall-clock time in a NAMED zone, not the reader's own.
 *
 * A cohort carries an IANA `timezone` and the API sends `held_at` as an
 * instant, deliberately: a facilitator in Amman and a coordinator in Berlin must
 * see the same session at the same hour of the same evening, and that hour is
 * the COHORT's, not the browser's. `formatDateTime` above is the right function
 * for a record's own audit stamps and the wrong one here.
 *
 * The zone name always renders beside the time (the caller is expected to show
 * `zoneLabel`), because "18:00" with no zone is the ambiguity this function
 * exists to remove.
 *
 * An unknown or malformed zone falls back to the browser's rather than throwing
 * — `Intl` rejects a bad `timeZone` with a RangeError, and a cohort whose zone
 * was typed by hand must not blank out its whole schedule.
 */
export function formatInZone(iso: string | null | undefined, timeZone?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const locale = i18n.language === 'ar' ? 'ar' : 'en-GB';
  for (const zone of [timeZone || undefined, undefined]) {
    try {
      return new Intl.DateTimeFormat(locale, {
        timeZone: zone,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        numberingSystem: 'latn',
      }).format(date);
    } catch {
      /* An invalid IANA name: try again with the browser's zone. */
    }
  }
  return formatDateTime(iso);
}

/**
 * Whether this browser's `Intl` can resolve an IANA zone name at all.
 *
 * Every other function here degrades gracefully when it cannot — and that is
 * the trap: `formatInZone` falls back to the reader's own zone, `zoneLabel` to
 * the bare city, `instantToZonedInput` to a blank field, so the screen shows a
 * time, a zone name and an empty input that disagree with each other and none
 * of them says which is lying. A caller that is about to WRITE, or that is
 * about to assert "times are shown in X", has to be able to ask.
 *
 * Not the same question as "does this cohort have a zone": no zone at all is a
 * legitimate state meaning "the reader's own", so an empty name is not unknown.
 *
 * The server validates the name against Go's tzdata (B-30), which does not close
 * this: the IANA database renames and drops zones — `Europe/Kyiv` only exists
 * from 2022 and a browser a year behind knows `Europe/Kiev` alone — so a zone
 * that passed validation on the server can still fail to resolve here, and rows
 * written before that validation existed are grandfathered.
 */
const zoneResolves = new Map<string, boolean>();
export function isZoneUnknown(timeZone: string | null | undefined): boolean {
  if (!timeZone) return false;
  let known = zoneResolves.get(timeZone);
  if (known === undefined) {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone });
      known = true;
    } catch {
      known = false;
    }
    zoneResolves.set(timeZone, known);
  }
  return !known;
}

/**
 * How a zone is named to a reader: `Asia/Amman` becomes `Amman (GMT+3)`. The
 * offset is resolved AT the given instant so a session in July and one in
 * January read correctly across a DST boundary.
 */
export function zoneLabel(timeZone: string | null | undefined, at?: string | null): string {
  if (!timeZone) return '';
  const city = timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone;
  const date = at ? new Date(at) : new Date();
  if (Number.isNaN(date.getTime())) return city;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(date);
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
    return offset ? `${city} (${offset})` : city;
  } catch {
    return city;
  }
}

/**
 * The offset of `timeZone` at a given instant, in milliseconds.
 *
 * There is no API that hands this over, so it is derived the only way the
 * platform allows: format the instant IN the zone, read the wall clock back,
 * and subtract. `hour12: false` can yield hour `24` for midnight in some
 * engines, hence the `% 24`.
 */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour) % 24,
    Number(map.minute),
    Number(map.second),
  );
  return asUTC - date.getTime();
}

/**
 * An instant → the `YYYY-MM-DDTHH:mm` a `<input type="datetime-local">` wants,
 * read in the COHORT's zone rather than the browser's.
 *
 * A coordinator in Berlin editing a Warsaw cohort's session must see and type
 * the hour the session actually happens at in Warsaw. Feeding the raw instant
 * to the input would show them their own wall clock and silently shift the
 * session by an hour the moment they saved anything else on the form.
 *
 * Two different empties, and they must not be conflated — F-27 was exactly that
 * conflation, and it silently erased a session's schedule on a rename:
 *   `''`   — there is no instant. The field is legitimately blank, and a form
 *            that saves it blank is clearing a time that was never set.
 *   `null` — there IS an instant, and this browser cannot render it in that
 *            zone. The field cannot be shown; a form that reads this as a clear
 *            destroys a date nobody asked it to touch.
 */
export function instantToZonedInput(
  iso: string | null | undefined,
  timeZone?: string | null,
): string | null {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || undefined,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(date);
    const map: Record<string, string> = {};
    for (const p of parts) map[p.type] = p.value;
    const hour = String(Number(map.hour) % 24).padStart(2, '0');
    return `${map.year}-${map.month}-${map.day}T${hour}:${map.minute}`;
  } catch {
    // A zone this browser does not know. The instant is intact and must be
    // preserved by the caller — see the two-empties note above.
    return null;
  }
}

/**
 * The inverse: a wall clock typed in the cohort's zone → the RFC 3339 instant
 * the API stores.
 *
 * The offset is applied twice on purpose. The first pass uses the offset at the
 * NAIVE instant, which is wrong by an hour for the two days a year a zone
 * changes; the second uses the offset at the corrected instant, which is right.
 * Without it, a session scheduled for the evening the clocks go back lands an
 * hour out — and that hour is a room full of children arriving to a locked door.
 */
export function zonedInputToInstant(local: string, timeZone?: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return null;
  const naive = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  // No zone on the cohort: the input means the reader's own wall clock, which
  // is exactly how `new Date('2026-06-01T18:00')` parses it.
  if (!timeZone) return new Date(local).toISOString();
  try {
    let ts = naive - zoneOffsetMs(new Date(naive), timeZone);
    ts = naive - zoneOffsetMs(new Date(ts), timeZone);
    return new Date(ts).toISOString();
  } catch {
    return new Date(naive).toISOString();
  }
}
